import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, IconButton, MenuItem, Modal, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import RadiusButton from '../../../../components/RadiusButton';
import { useToaster } from '../../../../providers/ToasterProvider';
import { createLocation, deleteLocation } from '../../../../services/venueManagementService';
import { DEFAULT_COUNTRY_NAME, LOCATION_NAME_MAX_LENGTH, LOCATION_TYPES } from './venueManagementConstants';

import SEARCH_ICON from '../../../../assets/images/search-icon.svg';
import ACTION_DELETE from '../../../../assets/images/action-delete.svg';
import ADD_USER from '../../../../assets/images/add-user.svg';

const DELETE_CONFIRM_TIMEOUT_MS = 3000;

const byName = (a, b) => a.name.localeCompare(b.name);
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// List, search, add and delete countries, states or cities. type: 'country' | 'state' | 'city'.
export const LocationModal = ({ open, type, locations, onClose, onChanged }) => {

    const { addNotification } = useToaster();
    const [view, setView] = useState('list');
    const [search, setSearch] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [notice, setNotice] = useState('');
    const [armedId, setArmedId] = useState(null);
    const [form, setForm] = useState({ countryId: '', stateId: '', name: '' });
    const [formErrors, setFormErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const nameInputRef = useRef(null);
    // Remembers the parent picked last, so adding several cities in a row keeps the same state.
    const lastParent = useRef({ countryId: '', stateId: '' });

    const typeInfo = LOCATION_TYPES[type] ?? LOCATION_TYPES.country;
    const { countries, states, cities } = locations;

    useEffect(() => {
        if (!open) return;
        setView('list');
        setSearch('');
        setCountryFilter('');
        setStateFilter('');
        setNotice('');
        setArmedId(null);
    }, [open, type]);

    useEffect(() => {
        if (!armedId) return;
        const timer = setTimeout(() => setArmedId(null), DELETE_CONFIRM_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [armedId]);

    const stateById = (id) => states.find((state) => state.id === id);

    const items = useMemo(() => {
        // Local lookups so the memo depends only on the lists it reads.
        const countryName = (id) => countries.find((country) => country.id === id)?.name ?? '';
        const stateById = (id) => states.find((state) => state.id === id);
        let rows = [];
        if (type === 'country') {
            rows = countries.map((country) => ({ ...country, sub: plural(country.stateCount, 'state', 'states') }));
        }
        if (type === 'state') {
            rows = states
                .filter((state) => !countryFilter || state.countryId === countryFilter)
                .map((state) => ({ ...state, sub: `${countryName(state.countryId)} · ${plural(state.cityCount, 'city', 'cities')}` }));
        }
        if (type === 'city') {
            rows = cities
                .filter((city) => {
                    const state = stateById(city.stateId);
                    return (!stateFilter || city.stateId === stateFilter) && (!countryFilter || state?.countryId === countryFilter);
                })
                .map((city) => {
                    const state = stateById(city.stateId);
                    return { ...city, sub: `${state?.name ?? ''}, ${countryName(state?.countryId)} · ${plural(city.venueCount, 'venue', 'venues')}` };
                });
        }
        return rows;
    }, [type, countries, states, cities, countryFilter, stateFilter]);

    const query = search.trim().toLowerCase();
    const visibleItems = items.filter((item) => !query || `${item.name} ${item.sub}`.toLowerCase().includes(query)).sort(byName);

    const statesOfCountry = (countryId) => states.filter((state) => state.countryId === countryId).sort(byName);

    const blockedReason = (item) => {
        if (type === 'country' && item.stateCount > 0) return `${item.name} has states. Delete them first.`;
        if (type === 'state' && item.cityCount > 0) return `${item.name} has cities. Delete them first.`;
        if (type === 'city' && item.venueCount > 0) return `${item.name} has venues. Delete them first.`;
        return '';
    };

    const onDelete = async (item) => {
        setNotice('');
        const reason = blockedReason(item);
        if (reason) {
            setNotice(reason);
            return;
        }
        if (armedId !== item.id) {
            setArmedId(item.id);
            return;
        }
        setArmedId(null);
        try {
            const res = await deleteLocation(type, item.id);
            if (res.code === 'OK') {
                addNotification(`${item.name} deleted.`, 'success');
                onChanged();
            }
        } catch (error) {
            setNotice(error?.response?.data?.message ?? `${item.name} could not be deleted.`);
            console.log('There is an error while deleting location', error);
        }
    }

    const openAdd = () => {
        const defaultCountryId = countries.find((country) => country.name.toLowerCase() === DEFAULT_COUNTRY_NAME.toLowerCase())?.id;
        const countryId = countryFilter || lastParent.current.countryId || defaultCountryId || (countries.length === 1 ? countries[0].id : '');
        const stateId = stateFilter || lastParent.current.stateId;
        setForm({
            countryId,
            stateId: type === 'city' && stateById(stateId)?.countryId === countryId ? stateId : '',
            name: '',
        });
        setFormErrors({});
        setView('add');
    }

    const saveLocation = async (addAnother) => {
        const name = form.name.trim().replace(/\s+/g, ' ');
        const errors = {};
        if (type !== 'country' && !form.countryId) errors.countryId = 'Choose a country.';
        if (type === 'city' && form.countryId && !form.stateId) errors.stateId = 'Choose a state.';
        if (!name) errors.name = `Enter the ${typeInfo.label.toLowerCase()} name.`;
        setFormErrors(errors);
        if (Object.keys(errors).length) return;

        const body = { name };
        if (type === 'state') body.countryId = form.countryId;
        if (type === 'city') body.stateId = form.stateId;

        setIsSaving(true);
        try {
            const res = await createLocation(type, body);
            if (res.code === 'OK') {
                lastParent.current = { countryId: form.countryId, stateId: form.stateId };
                addNotification(`${name} added.`, 'success');
                onChanged();
                if (addAnother) {
                    setForm({ ...form, name: '' });
                    nameInputRef.current?.focus();
                } else {
                    setView('list');
                }
            }
        } catch (error) {
            setFormErrors({ name: error?.response?.data?.message ?? `${name} could not be saved.` });
            console.log('There is an error while saving location', error);
        } finally {
            setIsSaving(false);
        }
    }

    const statesForForm = statesOfCountry(form.countryId);

    return (
        <Modal open={open} hideBackdrop={true} className='backdrop' onClose={onClose}>
            <Box className='modal-container venue-modal venue-modal--tall' sx={{ maxWidth: 520 }}>
                <Box className='venue-modal-header'>
                    <Typography variant='h6' component='h6' sx={{ color: 'primary.main' }}>
                        {view === 'list' ? typeInfo.title : `Add ${typeInfo.label.toLowerCase()}`}
                    </Typography>
                    <span className='venue-pill'>{locations[typeInfo.listKey].length}</span>
                    <IconButton size='small' sx={{ ml: 'auto' }} onClick={onClose} aria-label='Close'>✕</IconButton>
                </Box>

                {view === 'list' ? (
                    <Box className='venue-modal-body'>
                        <Box className='location-toolbar'>
                            <div className='search-wrapper venue-search venue-search--full'>
                                <input type='text' className='search-box' value={search} onChange={(e) => setSearch(e.target.value)}
                                    placeholder={`Search ${typeInfo.title.toLowerCase()}...`} aria-label={`Search ${typeInfo.title.toLowerCase()}`} autoFocus />
                                <img className='search-icon' src={SEARCH_ICON} alt='' />
                            </div>
                            <RadiusButton variant='contained' color='secondary' startIcon={<img src={ADD_USER} alt='' />} onClick={openAdd}
                                sx={{ textTransform: 'uppercase', fontWeight: 500, flex: 'none' }}>
                                Add {typeInfo.label}
                            </RadiusButton>
                        </Box>

                        {type !== 'country' && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField select size='small' fullWidth label='Country' value={countryFilter}
                                    onChange={(e) => { setCountryFilter(e.target.value); setStateFilter(''); }}>
                                    <MenuItem value=''>All countries</MenuItem>
                                    {[...countries].sort(byName).map((country) => <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>)}
                                </TextField>
                                {type === 'city' && (
                                    <TextField select size='small' fullWidth label='State' value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                                        <MenuItem value=''>All states</MenuItem>
                                        {states.filter((state) => !countryFilter || state.countryId === countryFilter).sort(byName)
                                            .map((state) => <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>)}
                                    </TextField>
                                )}
                            </Box>
                        )}

                        {notice && <Alert severity='error' sx={{ py: 0 }} onClose={() => setNotice('')}>{notice}</Alert>}

                        <ul className='location-list'>
                            {!visibleItems.length && (
                                <li className='location-empty'>{items.length ? 'No matches.' : `No ${typeInfo.title.toLowerCase()} yet.`}</li>
                            )}
                            {visibleItems.map((item) => (
                                <li key={item.id}>
                                    <span className='location-name'>
                                        <b>{item.name}</b>
                                        <small>{item.sub}</small>
                                    </span>
                                    {armedId === item.id ? (
                                        <Button size='small' color='error' variant='contained' disableElevation onClick={() => onDelete(item)}>Delete?</Button>
                                    ) : (
                                        <IconButton size='small' aria-label={`Delete ${item.name}`} title='Delete' onClick={() => onDelete(item)}>
                                            <img src={ACTION_DELETE} alt='' />
                                        </IconButton>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </Box>
                ) : (
                    <Box component='form' noValidate autoComplete='off' className='venue-modal-body'
                        onSubmit={(e) => { e.preventDefault(); saveLocation(false); }}>
                        <Button size='small' sx={{ alignSelf: 'flex-start', textTransform: 'none' }} onClick={() => setView('list')}>← Back</Button>

                        {type !== 'country' && (
                            <TextField select size='small' fullWidth value={form.countryId}
                                label={<>Country<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                error={Boolean(formErrors.countryId)} helperText={formErrors.countryId}
                                onChange={(e) => { setForm({ ...form, countryId: e.target.value, stateId: '' }); setFormErrors({ ...formErrors, countryId: '' }); }}>
                                {[...countries].sort(byName).map((country) => <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>)}
                            </TextField>
                        )}

                        {type === 'city' && (
                            <TextField select size='small' fullWidth value={form.stateId}
                                disabled={!form.countryId || !statesForForm.length}
                                label={<>State<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                error={Boolean(formErrors.stateId)}
                                helperText={formErrors.stateId || (!form.countryId ? 'Select a country first' : !statesForForm.length ? 'No states in this country' : '')}
                                onChange={(e) => { setForm({ ...form, stateId: e.target.value }); setFormErrors({ ...formErrors, stateId: '' }); }}>
                                {statesForForm.map((state) => <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>)}
                            </TextField>
                        )}

                        <TextField size='small' fullWidth value={form.name} inputRef={nameInputRef} autoFocus
                            label={<>{typeInfo.label} name<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                            inputProps={{ maxLength: LOCATION_NAME_MAX_LENGTH }}
                            error={Boolean(formErrors.name)} helperText={formErrors.name}
                            onChange={(e) => { setForm({ ...form, name: e.target.value }); setFormErrors({ ...formErrors, name: '' }); }} />

                        <Box className='venue-form-actions'>
                            <RadiusButton variant='contained' color='grey' disabled={isSaving} onClick={() => setView('list')}>Cancel</RadiusButton>
                            <RadiusButton variant='outlined' disabled={isSaving} onClick={() => saveLocation(true)}>Save &amp; add another</RadiusButton>
                            <LoadingButton type='submit' variant='contained' loading={isSaving} sx={{ borderRadius: 40, textTransform: 'capitalize' }}>Save</LoadingButton>
                        </Box>
                    </Box>
                )}
            </Box>
        </Modal>
    );
}
